import React, { useCallback, useRef, useState } from 'react';
import { UploadCloud, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { UploadProgress } from '../../types/storage';

interface FileUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  uploads: UploadProgress[];
  onClearCompleted: () => void;
  disabled?: boolean;
}

export default function FileUploadZone({
  onFilesSelected,
  uploads,
  onClearCompleted,
  disabled = false,
}: FileUploadZoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length) onFilesSelected(files);
    },
    [onFilesSelected, disabled]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onFilesSelected(files);
    e.target.value = '';
  };

  const activeUploads = uploads.filter((u) => u.status !== 'done');
  const hasCompleted = uploads.some((u) => u.status === 'done');

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
          disabled
            ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
            : dragging
            ? 'border-indigo-400 bg-indigo-50'
            : 'border-gray-300 hover:border-indigo-300 hover:bg-gray-50'
        }`}
      >
        <UploadCloud
          className={`w-8 h-8 ${dragging ? 'text-indigo-500' : 'text-gray-400'}`}
        />
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700">
            {dragging ? 'Drop files here' : 'Drag & drop files, or click to browse'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Max 100 MB per file</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleChange}
          disabled={disabled}
        />
      </div>

      {uploads.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">Uploads</span>
            {hasCompleted && (
              <button
                onClick={onClearCompleted}
                className="text-xs text-indigo-600 hover:underline"
              >
                Clear completed
              </button>
            )}
          </div>
          {uploads.map((u) => (
            <UploadItem key={u.fileName} upload={u} />
          ))}
        </div>
      )}
    </div>
  );
}

function UploadItem({ upload }: { upload: UploadProgress }) {
  const icon = {
    pending: <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />,
    uploading: <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />,
    done: <CheckCircle className="w-4 h-4 text-green-500" />,
    error: <AlertCircle className="w-4 h-4 text-red-500" />,
  }[upload.status];

  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
      {icon}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-700 truncate">{upload.fileName}</p>
        {upload.status === 'uploading' && (
          <div className="mt-1 h-1 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-all duration-300 rounded-full"
              style={{ width: `${upload.progress}%` }}
            />
          </div>
        )}
        {upload.status === 'error' && (
          <p className="text-xs text-red-500 mt-0.5">{upload.error}</p>
        )}
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0">
        {upload.status === 'uploading' ? `${upload.progress}%` : upload.status}
      </span>
    </div>
  );
}
